/**
 * Today marker for date-ordered lists, plus local-day helpers.
 *
 * Charts deliberately have no Today line: their last point is always today,
 * so a line there says nothing (Eric, 2026-09-27). Lists that run past today
 * (the rate-assignment timeline) show the horizontal TodayDivider.
 */

export const TODAY_LINE_COLOR = '#8FB560';

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a day as LOCAL midnight. `new Date('2026-09-27')` is UTC midnight,
 * which reads as Sep 26 anywhere west of Greenwich — so date-only strings
 * are taken literally instead.
 */
export function parseLocalDay(value: string | Date): Date {
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const m = DATE_ONLY.exec(value);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Short axis label for a day, e.g. "Sep 27" (local). */
export function formatDayLabel(value: string | Date): string {
  return parseLocalDay(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    if (node === document.body || node === document.documentElement) return null;
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/** Centre `el` in its nearest scrollable container. Returns false when there is none. */
export function scrollToToday(el: HTMLElement): boolean {
  const parent = scrollParent(el);
  if (!parent) return false;
  const p = parent.getBoundingClientRect();
  const e = el.getBoundingClientRect();
  parent.scrollTop += e.top - p.top - (parent.clientHeight / 2 - e.height / 2);
  return true;
}
